/* eslint-disable */
import {
  menu,
  popMenu,
  type PopupTarget,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { TextBackgroundDuotoneIcon } from '@blocksuite/affine-components/icons';
import { DefaultInlineManagerExtension } from '@blocksuite/affine-inline-preset';
import type { TableColumn, TableRow } from '@blocksuite/affine-model';
import { RichText } from '@blocksuite/affine-rich-text';
import { cssVarV2 } from '@blocksuite/affine-shared/theme';
import { getViewportElement } from '@blocksuite/affine-shared/utils';
import { IS_MAC } from '@blocksuite/global/env';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import {
  ArrowDownBigIcon,
  ArrowLeftBigIcon,
  ArrowRightBigIcon,
  ArrowUpBigIcon,
  CloseIcon,
  ColorPickerIcon,
  CopyIcon,
  DeleteIcon,
  DuplicateIcon,
  ImageIcon,
  InsertAboveIcon,
  InsertBelowIcon,
  InsertLeftIcon,
  InsertRightIcon,
  PasteIcon,
} from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import type { InlineRange } from '@blocksuite/std/inline';
import { ZERO_WIDTH_FOR_EMBED_NODE } from '@blocksuite/std/inline';
import type { Text } from '@blocksuite/store';
import { computed, effect, signal } from '@preact/signals-core';
import { html, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';

import { colorList } from './color';
import { ColumnMaxWidth, DefaultColumnWidth } from './consts';
import type { SelectionController } from './selection-controller';
import {
  type TableAreaSelection,
  TableSelectionData,
} from './selection-schema';
import type { TableBlockComponent } from './table-block';
import {
  cellContainerStyle,
  columnLeftIndicatorStyle,
  columnOptionsCellStyle,
  columnOptionsStyle,
  columnRightIndicatorStyle,
  rowBottomIndicatorStyle,
  rowOptionsCellStyle,
  rowOptionsStyle,
  rowTopIndicatorStyle,
  threePointerIconDotStyle,
  threePointerIconStyle,
} from './table-cell-css';
import type { TableDataManager } from './table-data-manager';
import { pickTableDataFrom, getClipboardHtml, getClipboardText } from './utils';
import { getChatCustomApiBaseUrl } from '../../../../../packages/frontend/core/src/modules/cloud/constant';

export const TableCellComponentName = 'affine-table-cell';
export class TableCell extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @property({ attribute: false })
  accessor text: Text | undefined = undefined;

  private lastInlineRange: InlineRange | null = null;

  get readonly() {
    return this.dataManager.readonly$.value;
  }

  @property({ attribute: false })
  accessor dataManager!: TableDataManager;

  @query('rich-text')
  accessor richText: RichText | null = null;

  @property({ type: Number })
  accessor rowIndex = 0;

  @property({ type: Number })
  accessor columnIndex = 0;

  @property({ attribute: false })
  accessor row: TableRow | undefined = undefined;

  @property({ attribute: false })
  accessor column: TableColumn | undefined = undefined;

  @property({ attribute: false })
  accessor selectionController!: SelectionController;

  @property({ attribute: false })
  accessor height: number | undefined;

  get hoverColumnIndex$() {
    return this.dataManager.hoverColumnIndex$;
  }
  get hoverRowIndex$() {
    return this.dataManager.hoverRowIndex$;
  }
  get inlineManager() {
    return this.closest<TableBlockComponent>('affine-table')?.std.get(
      DefaultInlineManagerExtension.identifier
    );
  }

  get topContenteditableElement() {
    return this.closest<TableBlockComponent>('affine-table')
      ?.topContenteditableElement;
  }

  openColumnOptions(
    target: PopupTarget,
    column: TableColumn,
    columnIndex: number
  ) {
    this.selectionController.setSelected({
      type: 'column',
      columnId: column.columnId,
    });
    popMenu(target, {
      options: {
        onClose: () => {
          this.selectionController.setSelected(undefined);
        },
        items: [
          menu.group({
            items: [
              menu.subMenu({
                name: 'Background color',
                prefix: ColorPickerIcon(),
                options: {
                  items: [
                    { name: 'Default', color: undefined },
                    ...colorList,
                  ].map(item =>
                    menu.action({
                      prefix: html`<div
                        style="color: ${item.color ??
                        cssVarV2.layer.background
                          .primary};display: flex;align-items: center;justify-content: center;"
                      >
                        ${TextBackgroundDuotoneIcon}
                      </div>`,
                      name: item.name,
                      isSelected: column.backgroundColor === item.color,
                      select: () => {
                        this.dataManager.setColumnBackgroundColor(
                          column.columnId,
                          item.color
                        );
                      },
                    })
                  ),
                },
              }),
              ...(column.backgroundColor
                ? [
                  menu.action({
                    name: 'Clear column style',
                    prefix: CloseIcon(),
                    select: () => {
                      this.dataManager.setColumnBackgroundColor(
                        column.columnId,
                        undefined
                      );
                    },
                  }),
                ]
                : []),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: 'Insert Left',
                prefix: InsertLeftIcon(),
                select: () => {
                  this.dataManager.insertColumn(
                    columnIndex > 0 ? columnIndex - 1 : undefined
                  );
                },
              }),
              menu.action({
                name: 'Insert Right',
                prefix: InsertRightIcon(),
                select: () => {
                  this.dataManager.insertColumn(columnIndex);
                },
              }),
              menu.action({
                name: 'Move Left',
                prefix: ArrowLeftBigIcon(),
                select: () => {
                  this.dataManager.moveColumn(columnIndex, columnIndex - 2);
                },
              }),
              menu.action({
                name: 'Move Right',
                prefix: ArrowRightBigIcon(),
                select: () => {
                  this.dataManager.moveColumn(columnIndex, columnIndex + 1);
                },
              }),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: 'Duplicate',
                prefix: DuplicateIcon(),
                select: () => {
                  this.dataManager.duplicateColumn(columnIndex);
                },
              }),

              menu.action({
                name: 'Clear column contents',
                prefix: CloseIcon(),
                select: () => {
                  this.dataManager.clearColumn(column.columnId);
                },
              }),

              menu.action({
                name: 'Delete',
                class: {
                  'delete-item': true,
                },
                prefix: DeleteIcon(),
                select: () => {
                  this.dataManager.deleteColumn(column.columnId);
                },
              }),
            ],
          }),
        ],
      },
    });
  }

  openRowOptions(target: PopupTarget, row: TableRow, rowIndex: number) {
    this.selectionController.setSelected({
      type: 'row',
      rowId: row.rowId,
    });
    popMenu(target, {
      options: {
        onClose: () => {
          this.selectionController.setSelected(undefined);
        },
        items: [
          menu.group({
            items: [
              menu.subMenu({
                name: 'Background color',
                prefix: ColorPickerIcon(),
                options: {
                  items: [
                    { name: 'Default', color: undefined },
                    ...colorList,
                  ].map(item =>
                    menu.action({
                      prefix: html`<div
                        style="color: ${item.color ??
                        cssVarV2.layer.background
                          .primary};display: flex;align-items: center;justify-content: center;"
                      >
                        ${TextBackgroundDuotoneIcon}
                      </div>`,
                      name: item.name,
                      isSelected: row.backgroundColor === item.color,
                      select: () => {
                        this.dataManager.setRowBackgroundColor(
                          row.rowId,
                          item.color
                        );
                      },
                    })
                  ),
                },
              }),
              ...(row.backgroundColor
                ? [
                  menu.action({
                    name: 'Clear row style',
                    prefix: CloseIcon(),
                    select: () => {
                      this.dataManager.setRowBackgroundColor(
                        row.rowId,
                        undefined
                      );
                    },
                  }),
                ]
                : []),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: 'Insert Above',
                prefix: InsertAboveIcon(),
                select: () => {
                  this.dataManager.insertRow(
                    rowIndex > 0 ? rowIndex - 1 : undefined
                  );
                },
              }),
              menu.action({
                name: 'Insert Below',
                prefix: InsertBelowIcon(),
                select: () => {
                  this.dataManager.insertRow(rowIndex);
                },
              }),
              menu.action({
                name: 'Move Up',
                prefix: ArrowUpBigIcon(),
                select: () => {
                  this.dataManager.moveRow(rowIndex, rowIndex - 1);
                },
              }),
              menu.action({
                name: 'Move Down',
                prefix: ArrowDownBigIcon(),
                select: () => {
                  this.dataManager.moveRow(rowIndex, rowIndex + 1);
                },
              }),
            ],
          }),
          menu.group({
            items: [
              menu.action({
                name: 'Duplicate',
                prefix: DuplicateIcon(),
                select: () => {
                  this.dataManager.duplicateRow(rowIndex);
                },
              }),
              menu.action({
                name: 'Clear row contents',
                prefix: CloseIcon(),
                select: () => {
                  this.dataManager.clearRow(row.rowId);
                },
              }),
              menu.action({
                name: 'Delete',
                class: {
                  'delete-item': true,
                },
                prefix: DeleteIcon(),
                select: () => {
                  this.dataManager.deleteRow(row.rowId);
                },
              }),
            ],
          }),
        ],
      },
    });
  }

  createColorPickerMenu(
    currentColor: string | undefined,
    select: (color?: string) => void
  ) {
    return menu.subMenu({
      name: 'Background color',
      prefix: ColorPickerIcon(),
      options: {
        items: [{ name: 'Default', color: undefined }, ...colorList].map(item =>
          menu.action({
            prefix: html`<div
              style="color: ${item.color ??
              cssVarV2.layer.background
                .primary};display: flex;align-items: center;justify-content: center;"
            >
              ${TextBackgroundDuotoneIcon}
            </div>`,
            name: item.name,
            isSelected: currentColor === item.color,
            select: () => {
              select(item.color);
            },
          })
        ),
      },
    });
  }

  onContextMenu(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    const selected = this.selectionController.selected$.value;
    if (!selected) {
      return;
    }
    if (selected.type === 'area' && e.currentTarget instanceof HTMLElement) {
      const target = popupTargetFromElement(e.currentTarget);
      popMenu(target, {
        options: {
          items: [
            menu.group({
              items: [
                menu.action({
                  name: 'Copy',
                  prefix: CopyIcon(),
                  select: () => {
                    this.selectionController.doCopyOrCut(selected, false);
                  },
                }),
                menu.action({
                  name: 'Paste',
                  prefix: PasteIcon(),
                  select: () => {
                    this.handlePaste(selected).catch(console.error);
                  },
                }),
                menu.action({
                  name: 'Insert image',
                  prefix: ImageIcon(),
                  select: () => {
                    this.pickAndInsertImages().catch(console.error);
                  },
                }),
              ],
            }),
            menu.group({
              items: [
                menu.action({
                  name: 'Clear contents',
                  prefix: CloseIcon(),
                  select: () => {
                    this.dataManager.clearCellsBySelection(selected);
                  },
                }),
              ],
            }),
          ],
        },
      });
    }
  }

  renderColumnOptions(column: TableColumn, columnIndex: number) {
    const openColumnOptions = (e: Event) => {
      e.stopPropagation();
      const element = e.currentTarget;
      if (element instanceof HTMLElement) {
        this.openColumnOptions(
          popupTargetFromElement(element),
          column,
          columnIndex
        );
      }
    };
    return html`<div class=${columnOptionsCellStyle}>
      <div
        data-testid="drag-column-handle"
        data-drag-column-id=${column.columnId}
        class=${classMap({
      [columnOptionsStyle]: true,
    })}
        style=${styleMap({
      opacity: columnIndex === this.hoverColumnIndex$.value ? 1 : undefined,
    })}
        @click=${openColumnOptions}
      >
        ${threePointerIcon()}
      </div>
    </div>`;
  }

  renderRowOptions(row: TableRow, rowIndex: number) {
    const openRowOptions = (e: Event) => {
      e.stopPropagation();
      const element = e.currentTarget;
      if (element instanceof HTMLElement) {
        this.openRowOptions(popupTargetFromElement(element), row, rowIndex);
      }
    };
    return html`<div class=${rowOptionsCellStyle}>
      <div
        data-testid="drag-row-handle"
        data-drag-row-id=${row.rowId}
        class=${classMap({
      [rowOptionsStyle]: true,
    })}
        style=${styleMap({
      opacity: rowIndex === this.hoverRowIndex$.value ? 1 : undefined,
    })}
        @click=${openRowOptions}
      >
        ${threePointerIcon(true)}
      </div>
    </div>`;
  }
  renderOptionsButton() {
    if (this.readonly || !this.row || !this.column) {
      return nothing;
    }
    return html`
      ${this.rowIndex === 0
        ? this.renderColumnOptions(this.column, this.columnIndex)
        : nothing}
      ${this.columnIndex === 0
        ? this.renderRowOptions(this.row, this.rowIndex)
        : nothing}
    `;
  }

  tdMouseEnter(rowIndex: number, columnIndex: number) {
    this.hoverColumnIndex$.value = columnIndex;
    this.hoverRowIndex$.value = rowIndex;
  }

  tdMouseLeave() {
    this.hoverColumnIndex$.value = undefined;
    this.hoverRowIndex$.value = undefined;
  }
  isOccuredInSingleCellSelection(selected: TableAreaSelection | undefined) {
    // 检查是否为单单元格选择
    const isSingleCellSelection =
      selected &&
      selected.type === 'area' &&
      selected.rowStartIndex === selected.rowEndIndex &&
      selected.columnStartIndex === selected.columnEndIndex;

    if (!isSingleCellSelection) {
      return false;
    }

    // 检查焦点是否在当前单元格中
    const isFocusInCurrentCell = this.isFocusInCurrentCell();

    return isSingleCellSelection && isFocusInCurrentCell;
  }

  // 检查焦点是否在当前单元格中
  private isFocusInCurrentCell(): boolean {
    // 方法1: 检查 document.activeElement 是否是当前单元格或其子元素
    if (document.activeElement && this.contains(document.activeElement)) {
      return true;
    }

    // 方法2: 检查 inline editor 是否有焦点
    if (this.inlineEditor) {
      const inlineRange = this.inlineEditor.inlineRange$.value;
      return inlineRange !== null;
    }

    // 方法3: 检查 rich-text 元素是否有焦点
    if (this.richText$.value) {
      const richTextElement = this.richText$.value.shadowRoot?.activeElement;
      if (richTextElement) {
        return true;
      }
    }

    return false;
  }

  /**
   * 统一的粘贴处理方法，优雅地处理HTML和文本兜底逻辑
   */
  private async handlePaste(selected: TableAreaSelection) {
    try {
      const isOccuredInSingleCellSelection =
        this.isOccuredInSingleCellSelection(selected);
      const clipboardHtml = await getClipboardHtml();
      const tableData = clipboardHtml ? pickTableDataFrom(clipboardHtml) : null;
      const pasteContent = tableData ?? (await getClipboardText());

      if (isOccuredInSingleCellSelection) {
        this.selectionController.doPasteWithExpansion(
          pasteContent,
          selected.rowStartIndex,
          selected.columnStartIndex
        );
      } else {
        this.selectionController.doPaste(pasteContent, selected);
      }
      // 其他交互：如果任何paste都强制扩展表格，则使用下面片段
      // this.selectionController.doPasteWithExpansion(
      //   pasteContent,
      //   selected.rowStartIndex,
      //   selected.columnStartIndex
      // );

      // 粘贴完成后清除选择状态
      this.selectionController.setSelected(undefined);
    } catch (error) {
      console.warn('Failed to handle paste:', error);
      // 兜底：尝试获取纯文本
      try {
        const plainText = await getClipboardText();
        this.selectionController.doPaste(plainText, selected);
        // 粘贴完成后清除选择状态
        this.selectionController.setSelected(undefined);
      } catch (fallbackError) {
        console.warn('Failed to read clipboard content:', fallbackError);
      }
    }
  }

  private async pickAndInsertImages() {
    if (this.readonly) {
      return;
    }
    const inlineEditor = this.inlineEditor;
    const anchorRange = inlineEditor?.getInlineRange() ?? this.lastInlineRange;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = () => {
      const files = input.files;
      if (files && files.length) {
        this.insertInlineImages(Array.from(files), anchorRange).catch(
          console.error
        );
      }
    };
    input.click();
  }

  private async insertInlineImages(
    files: File[],
    anchorRange?: InlineRange | null
  ) {
    if (this.readonly) {
      return;
    }
    const inlineEditor = this.inlineEditor;
    const table = this.closest<TableBlockComponent>('affine-table');
    const std = table?.std;
    if (!inlineEditor || !std) {
      return;
    }

    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    if (!imageFiles.length) {
      return;
    }

    let inlineRange = anchorRange ?? inlineEditor.getInlineRange() ?? undefined;
    if (!inlineRange) {
      inlineEditor.focusEnd();
      inlineRange = inlineEditor.getInlineRange() ?? undefined;
    }
    if (!inlineRange) return;
    inlineEditor.setInlineRange(inlineRange);

    let isFirstImage = true;
    for (const file of imageFiles) {
      let currentRange: InlineRange = {
        index: inlineRange.index,
        length: 0,
      };

      // 确保第一张图片前面有一个换行，这样图片块与前后文字有明确的分隔
      if (isFirstImage) {
        const full = inlineEditor.yTextString;
        if (currentRange.index > 0 && full[currentRange.index - 1] !== '\n') {
          inlineEditor.insertText(
            { index: currentRange.index, length: 0 },
            '\n'
          );
          currentRange = {
            index: currentRange.index + 1,
            length: 0,
          };
        }
        isFirstImage = false;
      }

      // 插入占位，展示上传中
      inlineEditor.insertText(currentRange, ZERO_WIDTH_FOR_EMBED_NODE, {
        image: {
          src: '',
          title: file.name ?? '',
          alt: file.name ?? '',
          loading: true,
          error: undefined,
        },
      } as any);

      const placeholderIndex = currentRange.index;
      this.uploadAndReplaceImage(file, placeholderIndex).catch(err => {
        inlineEditor.formatText({ index: placeholderIndex, length: 1 }, {
          image: {
            loading: false,
            error: err?.message ?? 'upload failed',
          },
        } as any);
      });

      // 确保每张图片后面至少有一个换行，用于把相邻图片/文本隔开一行
      let fullAfterInsert = inlineEditor.yTextString;
      const afterIndex = placeholderIndex + 1;
      if (
        afterIndex >= fullAfterInsert.length ||
        fullAfterInsert[afterIndex] !== '\n'
      ) {
        inlineEditor.insertText({ index: afterIndex, length: 0 }, '\n');
        fullAfterInsert = inlineEditor.yTextString;
      }

      inlineRange = {
        index: afterIndex + 1,
        length: 0,
      };
    }
  }

  private async uploadAndReplaceImage(file: File, index: number) {
    const inlineEditor = this.inlineEditor;
    if (!inlineEditor) return;

    try {
      // eslint-disable-next-line import/no-extraneous-dependencies
      const { uploadImages, setRuntimeConfig } = await import(
        '@ys/repo-ui/hooks'
      );
      const env = (process.env.CUSTOM_API_ENV || 'development').toLowerCase();
      const apiPrefix = env === 'prod' || env === 'production' ? 'prod' : 'dev';
      setRuntimeConfig({
        apiPrefix,
        baseUrl: getChatCustomApiBaseUrl(),
      });
      const [result] = await uploadImages(file);
      if (!result) {
        throw new Error('upload failed');
      }
      const src =
        result.cdnUrl ??
        result.raw?.preSign?.fileUrl ??
        result.raw?.preSign?.cdnUrl ??
        '';
      if (!src) {
        throw new Error('empty url');
      }
      inlineEditor.formatText({ index, length: 1 }, {
        image: {
          src,
          sourceId: result.fileId,
          title: file.name ?? '',
          alt: file.name ?? '',
          loading: false,
          error: undefined,
        },
      } as any);
    } catch (err) {
      inlineEditor.formatText({ index, length: 1 }, {
        image: {
          loading: false,
          error: (err as Error)?.message ?? 'upload failed',
        },
      } as any);
    }
  }

  virtualWidth$ = computed(() => {
    const virtualWidth = this.dataManager.virtualWidth$.value;
    if (!virtualWidth || this.column?.columnId !== virtualWidth.columnId) {
      return undefined;
    }
    return virtualWidth.width;
  });

  tdStyle() {
    const columnWidth = this.virtualWidth$.value ?? this.column?.width;
    const backgroundColor =
      this.column?.backgroundColor ?? this.row?.backgroundColor ?? undefined;
    return styleMap({
      backgroundColor,
      minWidth: columnWidth ? `${columnWidth}px` : `${DefaultColumnWidth}px`,
      maxWidth: columnWidth ? `${columnWidth}px` : `${ColumnMaxWidth}px`,
      // 防止单元格内容强制换行
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
  }

  showColumnIndicator$ = computed(() => {
    const indicatorIndex =
      this.dataManager.ui.columnIndicatorIndex$.value ?? -1;
    if (indicatorIndex === 0 && this.columnIndex === 0) {
      return 'left';
    }
    if (indicatorIndex - 1 === this.columnIndex) {
      return 'right';
    }
    return;
  });
  showRowIndicator$ = computed(() => {
    const indicatorIndex = this.dataManager.ui.rowIndicatorIndex$.value ?? -1;
    if (indicatorIndex === 0 && this.rowIndex === 0) {
      return 'top';
    }
    if (indicatorIndex - 1 === this.rowIndex) {
      return 'bottom';
    }
    return;
  });
  renderRowIndicator() {
    if (this.readonly) {
      return nothing;
    }
    const columnIndex = this.columnIndex;
    const isFirstColumn = columnIndex === 0;
    const isLastColumn =
      columnIndex === this.dataManager.uiColumns$.value.length - 1;
    const showIndicator = this.showRowIndicator$.value;
    const style = (show: boolean) =>
      styleMap({
        opacity: show ? 1 : 0,
        borderRadius: isFirstColumn
          ? '3px 0 0 3px'
          : isLastColumn
            ? '0 3px 3px 0'
            : '0',
      });
    const indicator0 =
      this.rowIndex === 0
        ? html`
            <div
              style=${style(showIndicator === 'top')}
              class=${rowTopIndicatorStyle}
            ></div>
          `
        : nothing;
    return html`
      ${indicator0}
      <div
        style=${style(showIndicator === 'bottom')}
        class=${rowBottomIndicatorStyle}
      ></div>
    `;
  }
  renderColumnIndicator() {
    if (this.readonly) {
      return nothing;
    }
    const hoverColumnId$ = this.dataManager.hoverDragHandleColumnId$;
    const draggingColumnId$ = this.dataManager.widthAdjustColumnId$;
    const rowIndex = this.rowIndex;
    const isFirstRow = rowIndex === 0;
    const isLastRow = rowIndex === this.dataManager.uiRows$.value.length - 1;
    const showWidthAdjustIndicator =
      draggingColumnId$.value === this.column?.columnId ||
      hoverColumnId$.value === this.column?.columnId;
    const showIndicator = this.showColumnIndicator$.value;
    const style = (show: boolean) =>
      styleMap({
        opacity: show ? 1 : 0,
        borderRadius: isFirstRow
          ? '3px 3px 0 0'
          : isLastRow
            ? '0 0 3px 3px'
            : '0',
      });
    const indicator0 =
      this.columnIndex === 0
        ? html`
            <div
              style=${style(showIndicator === 'left')}
              class=${columnLeftIndicatorStyle}
            ></div>
          `
        : nothing;
    const mouseEnter = () => {
      hoverColumnId$.value = this.column?.columnId;
    };
    const mouseLeave = () => {
      hoverColumnId$.value = undefined;
    };
    return html` ${indicator0}
      <div
        @mouseenter=${mouseEnter}
        @mouseleave=${mouseLeave}
        style=${style(showWidthAdjustIndicator || showIndicator === 'right')}
        data-width-adjust-column-id=${this.column?.columnId}
        class=${columnRightIndicatorStyle}
      ></div>`;
  }

  richText$ = signal<RichText>();

  get inlineEditor() {
    return this.richText$.value?.inlineEditor;
  }

  private readonly _handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') {
      if (e.key === 'Tab') {
        e.preventDefault();
        return;
      }
      e.stopPropagation();
    }

    // Handle paste shortcut (Ctrl+V / Cmd+V)
    if (e.key === 'v' && (IS_MAC ? e.metaKey : e.ctrlKey)) {
      e.preventDefault();
      e.stopPropagation();

      const selected = this.selectionController.selected$.value;
      if (selected && selected.type === 'area') {
        this.handlePaste(selected).catch(console.error);
      }
      return;
    }
  };

  override connectedCallback() {
    super.connectedCallback();
    if (this.readonly) {
      return;
    }
    const selectAll = (e: KeyboardEvent) => {
      if (e.key === 'a' && (IS_MAC ? e.metaKey : e.ctrlKey)) {
        e.stopPropagation();
        e.preventDefault();
        this.inlineEditor?.selectAll();
      }
    };
    this.disposables.addFromEvent(this, 'keydown', selectAll);
    this.disposables.addFromEvent(this, 'click', (e: MouseEvent) => {
      e.stopPropagation();
      requestAnimationFrame(() => {
        if (!this.inlineEditor?.inlineRange$.value) {
          this.inlineEditor?.focusEnd();
        }
      });
    });
  }

  override firstUpdated() {
    if (this.readonly) {
      return;
    }
    this.richText$.value?.updateComplete
      .then(() => {
        const inlineEditor = this.inlineEditor;
        if (inlineEditor) {
          this.disposables.add(
            inlineEditor.slots.keydown.subscribe(this._handleKeyDown)
          );
          // 记录最近一次有效的 inline 选区，防止右键后选区被清空
          this.disposables.add(
            effect(() => {
              const range = inlineEditor.inlineRange$.value;
              if (range) {
                this.lastInlineRange = range;
              }
            })
          );
        }

        this.disposables.add(
          effect(() => {
            const richText = this.richText$.value;
            const inlineEditor = this.inlineEditor;
            if (!richText || !inlineEditor) {
              return;
            }
            const inlineRange = inlineEditor.inlineRange$.value;
            if (inlineRange) {
              console.info(
                '[TABLE-INLINE-IMG] inlineRange',
                inlineRange.index,
                inlineRange.length
              );
              const full = inlineEditor.yTextString;

              // 1. 优先尝试从 inline delta 中提取图片 / 视频信息构造 markdown
              const getMediaMarkdown = () => {
                const rangesToTry: Array<{ index: number; length: number }> =
                  inlineRange.length > 0
                    ? [inlineRange]
                    : [
                      inlineRange,
                      {
                        index: Math.max(0, inlineRange.index - 1),
                        length: 1,
                      },
                      { index: inlineRange.index, length: 1 },
                    ];

                for (const r of rangesToTry) {
                  const deltas = inlineEditor.getDeltasByInlineRange(r) ?? [];
                  const entry = deltas.find(
                    ([delta]: any) =>
                      delta?.attributes?.image?.src ||
                      delta?.attributes?.video?.src
                  ) as any;
                  const img = entry?.[0]?.attributes?.image;
                  if (img?.src) {
                    const alt = img.alt ?? '';
                    return `![${alt}](${img.src})`;
                  }
                  const video = entry?.[0]?.attributes?.video;
                  if (video?.src) {
                    const alt = video.alt ?? '';
                    return `![${alt}](${video.src})`;
                  }
                }
                // 再尝试直接取当前位置 delta
                const delta = inlineEditor.getDeltaByRangeIndex(
                  inlineRange.index
                ) as any;
                if (delta?.attributes?.image?.src) {
                  const img = delta.attributes.image;
                  const alt = img.alt ?? '';
                  return `![${alt}](${img.src})`;
                }
                if (delta?.attributes?.video?.src) {
                  const video = delta.attributes.video;
                  const alt = video.alt ?? '';
                  return `![${alt}](${video.src})`;
                }
                return '';
              };

              let selectionText = getMediaMarkdown();

              // 2. 如果不是图片 / 视频，再退回到纯文本逻辑
              if (!selectionText) {
                selectionText =
                  inlineRange.length > 0
                    ? full.slice(
                      inlineRange.index,
                      inlineRange.index + inlineRange.length
                    )
                    : '';

                // 过滤掉纯零宽 / 纯空白 / 纯换行，避免上报 `<br>`
                const isZeroWidthOrBlank =
                  !selectionText ||
                  selectionText === ZERO_WIDTH_FOR_EMBED_NODE ||
                  selectionText === '\u200B' ||
                  selectionText === '\uFEFF' ||
                  !selectionText.replace(/\s+/g, '');
                if (isZeroWidthOrBlank) {
                  selectionText = '';
                }
              }

              const svc = (window as any).__agentServiceBeta;
              if (svc?.emitSelectionFromEditor) {
                svc
                  .emitSelectionFromEditor({
                    selectionMarkdown: selectionText || full,
                    relativeStartLine: this.rowIndex + 1,
                  })
                  .catch(console.error);
              }
            } else {
              // inlineRange 不存在时尝试从 DOM selection 获取图片 / 视频信息
              try {
                const rootNode = richText.getRootNode();
                const sel =
                  rootNode instanceof Document || rootNode instanceof ShadowRoot
                    ? ((rootNode as Document).getSelection?.() ??
                      window.getSelection())
                    : window.getSelection();
                const node = sel?.focusNode ?? sel?.anchorNode;
                let el =
                  node instanceof Element
                    ? node
                    : (node?.parentElement ?? null);
                let mediaSrc: string | null = null;
                let mediaAlt: string | null = null;
                while (el) {
                  const img = el.getAttribute?.('data-inline-image-src');
                  const video = el.getAttribute?.('data-inline-video-src');
                  const src = img ?? video ?? null;
                  if (src) {
                    mediaSrc = src;
                    mediaAlt = el.getAttribute?.('alt') ?? '';
                    break;
                  }
                  el = el.parentElement;
                }
                if (mediaSrc) {
                  const selectionText = `![${mediaAlt ?? ''}](${mediaSrc})`;
                  console.info(
                    '[TABLE-INLINE-IMG] dom media selection -> markdown',
                    selectionText
                  );
                  const svc = (window as any).__agentServiceBeta;
                  if (svc?.emitSelectionFromEditor) {
                    svc
                      .emitSelectionFromEditor({
                        selectionMarkdown: selectionText,
                        relativeStartLine: 1,
                      })
                      .catch(console.error);
                  }
                } else {
                  console.info(
                    '[TABLE-INLINE-IMG] no inlineRange and no dom image'
                  );
                }
              } catch (e) {
                console.warn(
                  '[TABLE-INLINE-IMG] dom image selection parse failed',
                  e
                );
              }
            }
            const targetSelection: TableAreaSelection = {
              type: 'area',
              rowStartIndex: this.rowIndex,
              rowEndIndex: this.rowIndex,
              columnStartIndex: this.columnIndex,
              columnEndIndex: this.columnIndex,
            };
            const currentSelection = this.selectionController.selected$.peek();
            if (
              inlineRange &&
              !TableSelectionData.equals(targetSelection, currentSelection)
            ) {
              this.selectionController.setSelected(targetSelection, false);
            }
          })
        );
      })
      .catch(console.error);
  }

  override render() {
    if (!this.text) {
      return html`<td class=${cellContainerStyle} style=${this.tdStyle()}>
        <div
          style=${styleMap({
        padding: '8px 12px',
      })}
        >
          <div style="height:22px"></div>
        </div>
      </td>`;
    }
    return html`
      <td
        data-row-id=${this.row?.rowId}
        data-column-id=${this.column?.columnId}
        @mouseenter=${() => {
        this.tdMouseEnter(this.rowIndex, this.columnIndex);
      }}
        @mouseleave=${() => {
        this.tdMouseLeave();
      }}
        @contextmenu=${this.onContextMenu}
        class=${cellContainerStyle}
        style=${this.tdStyle()}
      >
        <rich-text
          ${ref(this.richText$)}
          data-disable-ask-ai
          data-not-block-text
          style=${styleMap({
        minHeight: '22px',
        padding: '8px 12px',
      })}
          .yText="${this.text}"
          .inlineEventSource="${this.topContenteditableElement ?? nothing}"
          .attributesSchema="${this.inlineManager?.getSchema()}"
          .attributeRenderer="${this.inlineManager?.getRenderer()}"
          .embedChecker="${this.inlineManager?.embedChecker}"
          .markdownMatches="${this.inlineManager?.markdownMatches}"
          .readonly="${this.readonly}"
          .enableClipboard="${true}"
          .verticalScrollContainerGetter="${() =>
        this.topContenteditableElement?.host
          ? getViewportElement(this.topContenteditableElement.host)
          : null}"
          data-parent-flavour="affine:table"
        ></rich-text>
        ${this.renderOptionsButton()} ${this.renderColumnIndicator()}
        ${this.renderRowIndicator()}
      </td>
    `;
  }
}

export const createColumnDragPreview = (cells: TableCell[]) => {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.opacity = '0.8';
  container.style.display = 'flex';
  container.style.flexDirection = 'column';
  container.style.zIndex = '1000';
  container.style.boxShadow = '0 0 10px 0 rgba(0, 0, 0, 0.1)';
  container.style.backgroundColor = cssVarV2.layer.background.primary;
  cells.forEach((cell, index) => {
    const div = document.createElement('div');
    const td = cell.querySelector('td');
    if (index !== 0) {
      div.style.borderTop = `1px solid ${cssVarV2.layer.insideBorder.border}`;
    }
    if (td) {
      div.style.height = `${td.getBoundingClientRect().height}px`;
    }
    if (cell.text) {
      const text = new RichText();
      text.style.padding = '8px 12px';
      text.yText = cell.text;
      text.readonly = true;
      text.attributesSchema = cell.inlineManager?.getSchema();
      text.attributeRenderer = cell.inlineManager?.getRenderer();
      text.embedChecker = cell.inlineManager?.embedChecker ?? (() => false);
      div.append(text);
    }
    container.append(div);
  });
  return container;
};

export const createRowDragPreview = (cells: TableCell[]) => {
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.opacity = '0.8';
  container.style.display = 'flex';
  container.style.flexDirection = 'row';
  container.style.zIndex = '1000';
  container.style.boxShadow = '0 0 10px 0 rgba(0, 0, 0, 0.1)';
  container.style.backgroundColor = cssVarV2.layer.background.primary;
  cells.forEach((cell, index) => {
    const div = document.createElement('div');
    const td = cell.querySelector('td');
    if (index !== 0) {
      div.style.borderLeft = `1px solid ${cssVarV2.layer.insideBorder.border}`;
    }
    if (td) {
      div.style.width = `${td.getBoundingClientRect().width}px`;
    }
    if (cell.text) {
      const text = new RichText();
      text.style.padding = '8px 12px';
      text.yText = cell.text;
      text.readonly = true;
      text.attributesSchema = cell.inlineManager?.getSchema();
      text.attributeRenderer = cell.inlineManager?.getRenderer();
      text.embedChecker = cell.inlineManager?.embedChecker ?? (() => false);
      div.append(text);
    }
    container.append(div);
  });
  return container;
};

const threePointerIcon = (vertical: boolean = false) => {
  return html`
    <div
      class=${threePointerIconStyle}
      style=${styleMap({
    transform: vertical ? 'rotate(90deg)' : undefined,
  })}
    >
      <div class=${threePointerIconDotStyle}></div>
      <div class=${threePointerIconDotStyle}></div>
      <div class=${threePointerIconDotStyle}></div>
    </div>
  `;
};
declare global {
  interface HTMLElementTagNameMap {
    [TableCellComponentName]: TableCell;
  }
}
